import { IAppContext } from 'App/AppContext';
import { mapFromDashboard } from 'mappings/dashboardMapping';
import { mapFromObjective, mapFromObjectiveContext, mapToObjective } from 'mappings/objectiveMapping';
import Dashboard from 'models/dashboard';
import DashboardDTO from 'models/dto/dashboardDTO';
import ObjectiveContextDTO from 'models/dto/Objective/objectiveContextDTO';
import { ObjectiveDTO } from 'models/dto/Objective/objectiveDTO';
import { ObjectiveKPIDTO } from 'models/dto/Objective/objectiveKPIDTO';
import GlobalDataCache from 'models/globalDataCache/globalDataCache';
import Objective from 'models/objective/objective';
import ObjectiveContext from 'models/objective/objectiveContext';
import AppError from 'utils/appError';
import http from './httpService';

export async function apiGetObjectives(accessToken: string, globalDataCache: GlobalDataCache): Promise<Objective[]> {
  try {
    const ar = await http.get<ObjectiveDTO[]>(`/objectives`, http.getRequestConfig(accessToken));

    return ar.data.map((item) => mapFromObjective(item, globalDataCache));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetObjective(
  objectiveId: number,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Objective | undefined> {
  try {
    if (!objectiveId || objectiveId <= 0) return undefined;
    const ar = await http.get<ObjectiveDTO>(`/objectives/${objectiveId}`, http.getRequestConfig(accessToken));
    if (!ar.data) return undefined;

    return mapFromObjective(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetObjectivesForRisk(riskId: number, accessToken: string, globalDataCache: GlobalDataCache) {
  try {
    const ar = await http.get<ObjectiveDTO[]>(`/objectives/risk/${riskId}`, http.getRequestConfig(accessToken));

    return ar.data.map((item) => mapFromObjective(item, globalDataCache));
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddObjective(
  objective: Objective,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Objective> {
  try {
    const objectiveDTO = mapToObjective(objective);
    const ar = await http.post<ObjectiveDTO>(`/objectives`, objectiveDTO, http.getRequestConfig(accessToken));

    return mapFromObjective(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveObjective(objectiveId: number, accessToken: string): Promise<boolean> {
  try {
    await http.post<ObjectiveDTO>(`/objectives/delete/${objectiveId}`, null, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiUpdateObjective(
  objective: Objective,
  accessToken: string,
  globalDataCache: GlobalDataCache,
): Promise<Objective> {
  try {
    const objectiveDTO = mapToObjective(objective);
    const ar = await http.put<ObjectiveDTO>(`/objectives`, objectiveDTO, http.getRequestConfig(accessToken));

    return mapFromObjective(ar.data, globalDataCache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetObjectiveCode(accessToken: string, code: string): Promise<number> {
  try {
    const ar = await http.post<number>(`/objectives/code`, { valueString: code }, http.getRequestConfig(accessToken));

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetObjectiveContext(
  objectiveId: number,
  accessToken: string,
  appContext: IAppContext,
): Promise<ObjectiveContext> {
  try {
    const ar = await http.get<ObjectiveContextDTO>(
      `/objectives/context/${objectiveId}`,
      http.getRequestConfig(accessToken),
    );

    return mapFromObjectiveContext(ar.data, appContext);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiCreateObjectiveDashboard(
  objectiveId: number,
  accessToken: string,
  cache: GlobalDataCache,
): Promise<Dashboard> {
  try {
    const ar = await http.post<DashboardDTO>(
      `/objectives/${objectiveId}/dashboard`,
      null,
      http.getRequestConfig(accessToken),
    );

    return mapFromDashboard(ar.data, cache);
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiAddKPIToObjective(
  objectiveIdFrom: number,
  kpiIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ObjectiveKPIDTO(objectiveIdFrom, kpiIdTo);

    await http.post<ObjectiveKPIDTO>(`/objectives/kpi?add=${true}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiRemoveKPIFromObjective(
  objectiveIdFrom: number,
  kpiIdTo: number,
  accessToken: string,
): Promise<boolean> {
  try {
    const body = new ObjectiveKPIDTO(objectiveIdFrom, kpiIdTo);

    await http.post<ObjectiveKPIDTO>(`/objectives/kpi?add=${false}`, body, http.getRequestConfig(accessToken));

    return true;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}

export async function apiGetObjectiveExternalId(accessToken: string, externalId: string): Promise<number> {
  try {
    const ar = await http.post<number>(
      `/objectives/externalid`,
      { valueString: externalId },
      http.getRequestConfig(accessToken),
    );

    return ar.data;
  } catch (err) {
    throw AppError.fromApiError(err);
  }
}
